import { FC, useState, useCallback, KeyboardEvent } from 'react';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import Image from "next/legacy/image";
import { searchBarStyles } from './header-search-bar.styles';
import ImageWrapper from '../../images/Image-wrapper';
import searchIcon from '../../../public/assets/icons/search-icon-black.svg';
import { useStyles } from '../../../utils/hooks/useStyles';
import { useRouter } from 'next/router';
import useLink from '../../../utils/hooks/useLink';
import { useLocation } from '../../../contexts/locationContext';
import Showable from '../showable';
import CloseIcon from '@mui/icons-material/Close';

export interface SearchBarProps {
	placeholder?: string;
	label?: string;
	fullWidth?: boolean;
	onSubmit?(search: string): void;
	onClear?(): void;
}

const SearchBar: FC<SearchBarProps> = ({ placeholder, label, fullWidth, onSubmit, onClear }) => {
	const router = useRouter();
	const { search: searchParam, ...rest } = router.query as { search: string };
	const { makeSearchEventsLink } = useLink();
	const [search, setSearch] = useState<string>(searchParam);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	};

	const onSearchHandler = useCallback(
		(_event) => {
			if (onSubmit) {
				onSubmit(search);

				return;
			}

			router.push(makeSearchEventsLink({
				search,
				...router.pathname.startsWith('/search') ? rest : {},
			}));
		},
		[makeSearchEventsLink, router, search, onSubmit]
	);

	const onSearchValueClear = useCallback(() => {
		setSearch('');
		onClear?.();
	}, [onClear]);

	const keyDown = (event: KeyboardEvent) => {
		if (event.key === 'Enter') {
			onSearchHandler(event);
		}
	};

	const handleMouseDownClear = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const styles = useStyles(searchBarStyles, fullWidth);

	return (
		<Box sx={styles.root} onKeyDown={keyDown}>
			<Box sx={styles.fieldWrapper}>
				<TextField
					name="search"
					fullWidth={fullWidth}
					variant="standard"
					value={search}
					onChange={handleSearchChange}
					placeholder={placeholder ?? 'Search'}
					label={label}
					sx={styles.textField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<ImageWrapper
									sx={{
										display: 'flex',
										alignItems: 'center',
										color: 'black',
									}}
								>
									<Image src={searchIcon} alt="search" />
								</ImageWrapper>
							</InputAdornment>
						),
						endAdornment: (
							<Showable show={search}>
								<InputAdornment position="end">
									<IconButton
										sx={{
											color: 'black',
										}}
										aria-label="clear value"
										onClick={onSearchValueClear}
										onMouseDown={handleMouseDownClear}
									>
										<CloseIcon />
									</IconButton>
								</InputAdornment>
							</Showable>
						),
					}}
					inputProps={{
						style: styles.inputStyles,
					}}
				/>
			</Box>
		</Box>
	);
};

export default SearchBar;
